/**
 * Memory Hub-RPC payload / data schemas — handle / topic / tag shapes.
 *
 * The path-based memory shapes in `files.ts` have been retired with the
 * flat handle store. Workspace shapes still live in `files.ts` because
 * the workspace surface is unchanged.
 *
 * Inbound payload schemas use `.passthrough()` (tolerant reader); outbound
 * `*DataSchema` use `.strict()` (strict writer). Per
 * `docs/operations/contract-evolution.md` — applies even though the
 * contract-evolution policy is aspirational until #179 ships, so the wire
 * shape lands compliant.
 */

import { z } from "zod";
import { AgentIdSchema, IsoDateTimeSchema } from "./primitives.js";

// Kebab-case, mirroring the store's on-disk `HANDLE_RE`: the handle is joined
// into a filesystem path, so traversal characters must never reach it. Written
// with a mandatory hyphen per group (not `-?`) so it can't backtrack
// catastrophically on an in-bounds adversarial handle at this untrusted wire seam.
const HandleSchema = z
  .string()
  .min(1)
  .max(80)
  .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/);
const TopicIdSchema = z.string().min(1).max(80);
const TagNameSchema = z.string().min(1).max(80);

const TopicEntrySchema = z
  .object({
    id: TopicIdSchema,
    label: z.string(),
    description: z.string(),
    created: z.string(),
    entityCount: z.number().int().nonnegative(),
  })
  .strict();

const TagEntrySchema = z
  .object({
    name: TagNameSchema,
    description: z.string(),
    created: z.string(),
    entityCount: z.number().int().nonnegative(),
  })
  .strict();

const EntityListItemSchema = z
  .object({
    handle: HandleSchema,
    description: z.string(),
    topic: TopicIdSchema,
    tags: z.array(TagNameSchema),
    mtime: IsoDateTimeSchema,
  })
  .strict();

const RenameProgressSchema = z
  .object({
    ok: z.boolean(),
    changed: z.boolean(),
    completed: z.array(HandleSchema),
    pending: z.array(HandleSchema),
    failure: z
      .object({
        handle: z.string(),
        error: z.string(),
      })
      .strict()
      .optional(),
  })
  .strict();

const DeleteCatalogResultSchema = z
  .object({
    deleted: z.boolean(),
    reason: z.string().optional(),
    referencing: z.array(HandleSchema).optional(),
  })
  .strict();

/* --------------------------------------------------------------------- *
 * Payloads (inbound — tolerant reader)
 * --------------------------------------------------------------------- */

export const MemoryReadByHandlePayloadSchema = z
  .object({ agentId: AgentIdSchema, handle: HandleSchema })
  .passthrough();

export const MemoryWriteByHandlePayloadSchema = z
  .object({
    agentId: AgentIdSchema,
    handle: HandleSchema,
    description: z.string(),
    content: z.string(),
    topic: TopicIdSchema,
    tags: z.array(TagNameSchema).optional(),
  })
  .passthrough();

export const MemoryListByFilterPayloadSchema = z
  .object({
    agentId: AgentIdSchema,
    topic: TopicIdSchema.optional(),
    tag: TagNameSchema.optional(),
    limit: z.number().int().min(1).max(100).optional(),
    cursor: z.string().optional(),
  })
  .passthrough();

export const MemoryDeleteByHandlePayloadSchema = z
  .object({ agentId: AgentIdSchema, handle: HandleSchema })
  .passthrough();

export const MemoryListTopicsPayloadSchema = z.object({ agentId: AgentIdSchema }).passthrough();

export const MemoryListTagsPayloadSchema = z.object({ agentId: AgentIdSchema }).passthrough();

export const MemorySaveTopicPayloadSchema = z
  .object({
    agentId: AgentIdSchema,
    id: TopicIdSchema,
    label: z.string().min(1),
    description: z.string(),
  })
  .passthrough();

export const MemoryDeleteTopicPayloadSchema = z
  .object({ agentId: AgentIdSchema, id: TopicIdSchema })
  .passthrough();

export const MemorySaveTagPayloadSchema = z
  .object({
    agentId: AgentIdSchema,
    name: TagNameSchema,
    description: z.string().optional(),
  })
  .passthrough();

export const MemoryDeleteTagPayloadSchema = z
  .object({ agentId: AgentIdSchema, name: TagNameSchema })
  .passthrough();

export const MemoryRenameTopicPayloadSchema = z
  .object({
    agentId: AgentIdSchema,
    oldId: TopicIdSchema,
    newId: TopicIdSchema,
  })
  .passthrough();

export const MemoryRenameTagPayloadSchema = z
  .object({
    agentId: AgentIdSchema,
    oldName: TagNameSchema,
    newName: TagNameSchema,
  })
  .passthrough();

/* --------------------------------------------------------------------- *
 * Data (outbound — strict writer)
 * --------------------------------------------------------------------- */

export const MemoryReadByHandleDataSchema = z
  .object({
    handle: HandleSchema,
    description: z.string(),
    topic: TopicIdSchema,
    tags: z.array(TagNameSchema),
    body: z.string(),
    mtime: IsoDateTimeSchema,
  })
  .strict();

export const MemoryWriteByHandleDataSchema = z
  .object({
    created: z.boolean(),
    handle: HandleSchema,
  })
  .strict();

export const MemoryListByFilterDataSchema = z
  .object({
    entities: z.array(EntityListItemSchema),
    nextCursor: z.string().optional(),
  })
  .strict();

export const MemoryDeleteByHandleDataSchema = z.object({ ok: z.literal(true) }).strict();

export const MemoryListTopicsDataSchema = z.object({ topics: z.array(TopicEntrySchema) }).strict();

export const MemoryListTagsDataSchema = z.object({ tags: z.array(TagEntrySchema) }).strict();

export const MemorySaveTopicDataSchema = z
  .object({
    topic: z
      .object({
        id: TopicIdSchema,
        label: z.string(),
        description: z.string(),
        created: z.string(),
      })
      .strict(),
  })
  .strict();

export const MemoryDeleteTopicDataSchema = DeleteCatalogResultSchema;

export const MemorySaveTagDataSchema = z
  .object({
    tag: z
      .object({
        name: TagNameSchema,
        description: z.string(),
        created: z.string(),
      })
      .strict(),
  })
  .strict();

export const MemoryDeleteTagDataSchema = DeleteCatalogResultSchema;

export const MemoryRenameTopicDataSchema = RenameProgressSchema;

export const MemoryRenameTagDataSchema = RenameProgressSchema;

/* --------------------------------------------------------------------- *
 * Type exports for ergonomic consumption.
 * --------------------------------------------------------------------- */

export type MemoryReadByHandlePayload = z.infer<typeof MemoryReadByHandlePayloadSchema>;
export type MemoryWriteByHandlePayload = z.infer<typeof MemoryWriteByHandlePayloadSchema>;
export type MemoryListByFilterPayload = z.infer<typeof MemoryListByFilterPayloadSchema>;
export type MemoryDeleteByHandlePayload = z.infer<typeof MemoryDeleteByHandlePayloadSchema>;
export type MemoryListTopicsPayload = z.infer<typeof MemoryListTopicsPayloadSchema>;
export type MemoryListTagsPayload = z.infer<typeof MemoryListTagsPayloadSchema>;
export type MemorySaveTopicPayload = z.infer<typeof MemorySaveTopicPayloadSchema>;
export type MemoryDeleteTopicPayload = z.infer<typeof MemoryDeleteTopicPayloadSchema>;
export type MemorySaveTagPayload = z.infer<typeof MemorySaveTagPayloadSchema>;
export type MemoryDeleteTagPayload = z.infer<typeof MemoryDeleteTagPayloadSchema>;
export type MemoryRenameTopicPayload = z.infer<typeof MemoryRenameTopicPayloadSchema>;
export type MemoryRenameTagPayload = z.infer<typeof MemoryRenameTagPayloadSchema>;

export type MemoryReadByHandleData = z.infer<typeof MemoryReadByHandleDataSchema>;
export type MemoryWriteByHandleData = z.infer<typeof MemoryWriteByHandleDataSchema>;
export type MemoryListByFilterData = z.infer<typeof MemoryListByFilterDataSchema>;
export type MemoryDeleteByHandleData = z.infer<typeof MemoryDeleteByHandleDataSchema>;
export type MemoryListTopicsData = z.infer<typeof MemoryListTopicsDataSchema>;
export type MemoryListTagsData = z.infer<typeof MemoryListTagsDataSchema>;
export type MemorySaveTopicData = z.infer<typeof MemorySaveTopicDataSchema>;
export type MemoryDeleteTopicData = z.infer<typeof MemoryDeleteTopicDataSchema>;
export type MemorySaveTagData = z.infer<typeof MemorySaveTagDataSchema>;
export type MemoryDeleteTagData = z.infer<typeof MemoryDeleteTagDataSchema>;
export type MemoryRenameTopicData = z.infer<typeof MemoryRenameTopicDataSchema>;
export type MemoryRenameTagData = z.infer<typeof MemoryRenameTagDataSchema>;

export const WIKILINK_RE = /\[\[\s*([^[\]\n]+?)\s*\]\]/g;

export type ParsedWikilink =
  | { kind: "entity"; handle: string; label?: string }
  | { kind: "tag"; name: string; label?: string }
  | { kind: "topic"; id: string; label?: string };

export function parseWikilinks(body: string): ParsedWikilink[] {
  const wikilinkRe = new RegExp(WIKILINK_RE.source, WIKILINK_RE.flags);
  const out: ParsedWikilink[] = [];
  for (const match of body.matchAll(wikilinkRe)) {
    const parsed = parseOne(match[1]);
    if (parsed) out.push(parsed);
  }
  return out;
}

function parseOne(raw: string): ParsedWikilink | null {
  const trimmed = raw.trim();
  if (trimmed.length === 0) return null;
  const pipeIdx = trimmed.indexOf("|");
  const target = pipeIdx === -1 ? trimmed : trimmed.slice(0, pipeIdx).trim();
  const labelRaw = pipeIdx === -1 ? undefined : trimmed.slice(pipeIdx + 1).trim();
  const label = labelRaw && labelRaw.length > 0 ? labelRaw : undefined;

  if (target.startsWith("#")) {
    const name = target.slice(1).trim();
    if (!name) return null;
    return label ? { kind: "tag", name, label } : { kind: "tag", name };
  }
  if (target.startsWith("tag:")) {
    const name = target.slice(4).trim();
    if (!name) return null;
    return label ? { kind: "tag", name, label } : { kind: "tag", name };
  }
  if (target.startsWith("topic:")) {
    const id = target.slice(6).trim();
    if (!id) return null;
    return label ? { kind: "topic", id, label } : { kind: "topic", id };
  }
  const handle = target.replace(/^\/+/, "").replace(/\.md$/, "");
  if (!handle || handle.includes("/")) return null;
  return label ? { kind: "entity", handle, label } : { kind: "entity", handle };
}
